import i18next from 'i18next';
import React from 'react';
import Skeleton from 'react-loading-skeleton';
import { useFormContext } from 'react-hook-form';
import {
  ControlledInput,
  RichtextInput,
} from '../../../../../components/form7';
import { FieldInput7 } from '../../EmployeeData/Form/FieldInput7';
import {
  AvailableRecordTypeFragment,
  ExtraFieldsConfigReferenceInput,
  FieldType,
} from '../../../__generated__/graphql';
import { ExtraFieldLookup7 } from '../ExtraFieldLookup/ExtraFieldLookup7';
import { ExtraFieldsVisibility } from '../extraFieldsVisibility';
import { getExtraFieldInputName, recordTypeFormKey } from '../logic';
import { ExtraFieldContextParam, ExtraFieldFormInfoType } from '../types';
import { toHtmlInputStepAttribute } from './helpers';

export type Props = {
  availableRecordTypes: readonly AvailableRecordTypeFragment[];
  formFields: readonly ExtraFieldFormInfoType[];
  contextParams?: ExtraFieldContextParam[];
  disabled?: boolean;
  hideRecordType?: boolean;
  extraFieldsConfigReference?: ExtraFieldsConfigReferenceInput;
  hideEmptyLookups?: boolean;
};

export const ExtraFieldsAreaContent7: React.FC<
  React.PropsWithChildren<Props>
> = ({
  availableRecordTypes,
  formFields,
  contextParams,
  disabled,
  hideRecordType = false,
  extraFieldsConfigReference,
  hideEmptyLookups = false,
}) => {
  const { watch } = useFormContext();
  const recordTypeName = watch(recordTypeFormKey);

  if (!formFields.length) {
    return null;
  }
  const visibility = new ExtraFieldsVisibility();

  return (
    <>
      {!hideRecordType &&
        getRecordTypeFormControl7(availableRecordTypes, disabled)}
      {formFields
        .filter((field) =>
          visibility.isFieldVisible(field, {
            recordTypeName,
            contextParams: contextParams ?? [],
          }),
        )
        .map(
          ({
            fieldApiName,
            fieldInfo,
            required,
            id,
            visibleLines,
            disabled: fieldDisabled,
          }) => {
            const fieldDisabledValue = fieldDisabled ?? disabled ?? false;
            const inputFieldInfo = {
              name: getExtraFieldInputName(fieldApiName, fieldInfo.type),
              label: fieldInfo.label,
              type: fieldInfo.type,
              hint: fieldInfo.inlineHelpText ?? undefined,
              disabled: fieldDisabledValue,
              minRows: visibleLines ?? undefined,
              step: toHtmlInputStepAttribute(fieldInfo.decimalPlaces),
              required,
              options:
                fieldInfo.picklistValues?.map((picklistValue) => ({
                  label: picklistValue.label,
                  value: picklistValue.value,
                })) ?? [],
            };
            if (fieldInfo.type === 'REFERENCE') {
              return (
                <ExtraFieldLookup7
                  key={id}
                  fieldId={id}
                  disabled={fieldDisabledValue}
                  contextParams={contextParams}
                  fieldInfo={inputFieldInfo}
                  extraFieldsConfigReference={extraFieldsConfigReference}
                  hideWhenEmpty={hideEmptyLookups}
                />
              );
            }
            if (fieldInfo.type === 'TEXTAREA' && fieldInfo.isRichTextArea) {
              return (
                <RichtextInput
                  key={id}
                  name={inputFieldInfo.name}
                  label={inputFieldInfo.label}
                  required={required}
                  disabled={fieldDisabledValue}
                  hint={inputFieldInfo.hint}
                />
              );
            }
            return <FieldInput7 key={id} fieldInfo={inputFieldInfo} />;
          },
        )}
    </>
  );
};

export const Loading7: React.FC<React.PropsWithChildren<unknown>> = () => (
  <div className="my-3">
    <Skeleton height={40} className="mt-3" />
    <Skeleton height={40} className="mt-3" />
    <Skeleton height={40} className="mt-3" />
  </div>
);

export function getRecordTypeFormControl7(
  availableRecordTypes: readonly AvailableRecordTypeFragment[],
  disabled?: boolean,
) {
  if (availableRecordTypes.length === 0) {
    return <ControlledInput name={recordTypeFormKey} type="hidden" />;
  }
  const dropDownfieldInfo = {
    name: recordTypeFormKey,
    label: i18next.t('extraFields.recordType'),
    type: FieldType.Picklist,
    disabled: disabled || availableRecordTypes.length === 1,
    required: true,
    doNotInsertDefaultOptions: true,
    options: availableRecordTypes.map((recordType) => ({
      label: recordType.Name,
      value: recordType.DeveloperName,
    })),
  };
  return <FieldInput7 fieldInfo={dropDownfieldInfo} />;
}
